import Component from 'vue-class-component';
import TombolaBaseComponent from '@/components/base/base.component';
import { ICommandShortcutService, ICommandShortcutContext, CommandShortcutOrigin } from '@/components/chat/_domain/modules/command-shortcut-service';
import SearchResultsListComponent from '@/components/home/_components/search-bar/_components/search-results-list';
import { IServerServiceFactory, IChatServerService } from '@/app-modules/server-service';
import { SearchResult, SearchBarToggleVisibilityEventName, SearchBarFindUserEventName } from '@/components/home/_components/search-bar/_domain/models';
import { IUserSearchServiceFactory, IUserSearchService } from '@/components/chat/_domain/modules/user-search-service';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { ISinBanService } from '@/components/chat/_domain/modules/sin-ban-service';
import { IArcadeAdminFeedServiceProvider } from '@/components/chat/_domain/modules/arcade-admin-feed-service-provider';
import { IChatPrizeService } from '@/components/chat/_domain/modules/chat-prize-service';
import { IUserChatStatusService, UserChatStatus } from '@/components/chat/_domain/modules/user-chat-status-service/user-chat-status-service-interface';
import { User } from '@/app-modules/store/modules/user';
import { IPersistedStore } from '@/app-modules/store/persisted-store';

enum SearchStatus {
  Idle = 0,
  Searching = 1,
  Complete = 2
}

@Component({
  name: 'SearchBarComponent',
  components: {
    searchResultsList: SearchResultsListComponent
  }
})
export default class SearchBarComponent extends TombolaBaseComponent {
  private isActive: boolean = false;
  private searchString: string = '';
  private server: IChatServerService = null;
  private searchResults: SearchResult[] = [];
  private searchStatus: SearchStatus = SearchStatus.Idle;
  private searchBarToggleVisibilityEventHandler: () => void;
  private searchBarFindUserEventHandler: (searchTerm: string) => void;
  private cmUser: User = null;

  private get commandShortcutService(): ICommandShortcutService {
    return this.$container.get(SERVICE_IDENTIFIERS.ICommandShortcutService) as ICommandShortcutService;
  }

  private get serverServiceFactory(): IServerServiceFactory {
    return this.$container.get(SERVICE_IDENTIFIERS.IServerServiceFactory) as IServerServiceFactory;
  }

  private get userSearchServiceFactory(): IUserSearchServiceFactory {
    return this.$container.get(SERVICE_IDENTIFIERS.IUserSearchServiceFactory) as IUserSearchServiceFactory;
  }

  private get arcadeAdminFeedServiceProvider(): IArcadeAdminFeedServiceProvider {
    return this.$container.get(SERVICE_IDENTIFIERS.IArcadeAdminFeedServiceProvider) as IArcadeAdminFeedServiceProvider;
  }

  private get userChatStatusService(): IUserChatStatusService {
    return this.$container.get(SERVICE_IDENTIFIERS.IUserChatStatusService);
  }

  private get store(): IPersistedStore{
    return this.$container.get(SERVICE_IDENTIFIERS.IPersistedStore);
  }

  private get showLoadingSpinner(): boolean {
    return this.searchStatus === SearchStatus.Searching;
  }

  private get showSearchResults(): boolean {
    return this.searchStatus === SearchStatus.Complete;
  }

  private mounted() {
    this.initialiseCmChatServer();
    this.subscribeSearchBarEventHandlers();
    this.cmUser = this.store.Get(User);
  }

  private beforeDestroy() {
    this.unsubscribeSearchBarEventHandlers();
  }

  private initialiseCmChatServer(): void {
    this.serverServiceFactory.createChatService().then((response) => this.server = response);
  }

  private subscribeSearchBarEventHandlers(): void {
    this.searchBarToggleVisibilityEventHandler = this.handleSearchBarToggleVisibilityEvent.bind(this);
    this.searchBarFindUserEventHandler = this.handleSearchBarFindUserEvent.bind(this);
    this.$eventbus.$on(SearchBarToggleVisibilityEventName, this.searchBarToggleVisibilityEventHandler);
    this.$eventbus.$on(SearchBarFindUserEventName, this.searchBarFindUserEventHandler);
  }

  private unsubscribeSearchBarEventHandlers() {
    if (this.searchBarToggleVisibilityEventHandler) {
      this.$eventbus.$off(SearchBarToggleVisibilityEventName, this.searchBarToggleVisibilityEventHandler);
      this.searchBarToggleVisibilityEventHandler = null;
    }

    if (this.searchBarFindUserEventHandler) {
      this.$eventbus.$off(SearchBarFindUserEventName, this.searchBarFindUserEventHandler);
      this.searchBarFindUserEventHandler = null;
    }
  }

  private handleSearchBarToggleVisibilityEvent() {
    this.visibility();
  }

  private handleSearchBarFindUserEvent(searchTerm: string) {
    this.visibility(true);
    this.searchString = searchTerm || '';
    this.submitCommandAndKeepSearchString();
  }

  private visibility(isActive?: boolean) {
    if (isActive == null) isActive = !this.isActive;
    this.isActive = isActive;

    if (isActive) {
      this.$nextTick(() => this.focusSearchInput());
    } else {
      this.searchString = '';
      this.searchResults = [];
      this.searchStatus = SearchStatus.Idle;
    }
  }

  private focusSearchInput() {
    const searchInput = this.$refs.searchInput as HTMLElement;
    if (searchInput) {
      searchInput.focus();
    }
  }

  private submitCommand() {
    if (this.searchString) {
      this.submitCommandForProcessing();
      this.searchString = '';
    }
  }

  private submitCommandAndKeepSearchString() {
    if (this.searchString) {
      this.submitCommandForProcessing();
    }
  }

  private submitCommandForProcessing() {
    const context = this.getCommandShortcutContext();
    if (!this.commandShortcutService.processCommand(this.searchString, context)) {
      this.searchStatus = SearchStatus.Searching;

      const cmChatSearchPromise = this.searchUsingCmChatServer();
      const arcadeAdminFeedSearchPromise = this.searchUsingArcadeAdminFeed();
      Promise.all([cmChatSearchPromise, arcadeAdminFeedSearchPromise])
              .then((searchResultSets) => {
                this.searchResults = [];
                for (const searchResults of searchResultSets) {
                  this.searchResults.push(...searchResults.filter((o) => !this.searchResultExists(o)));
                }
                this.searchStatus = SearchStatus.Complete;
              });
    }
  }

  private searchResultExists(searchResult: SearchResult): boolean {
    return this.searchResults && this.searchResults.some((o) => o.equals(searchResult));
  }

  private searchUsingCmChatServer(): Promise<SearchResult[]> {
    return this.searchUsingChatServerService(this.server);
  }

  private searchUsingArcadeAdminFeed(): Promise<SearchResult[]> {
    const arcadeAdminFeedServerService = this.arcadeAdminFeedServiceProvider.getAdminFeedService();
    return this.searchUsingChatServerService(arcadeAdminFeedServerService);
  }

  private async searchUsingChatServerService(chatServerService: IChatServerService): Promise<SearchResult[]> {
    let userSearchService: IUserSearchService = null;
    try {
      userSearchService = this.userSearchServiceFactory.getUserSearchService(chatServerService);
      const results = await userSearchService.findUserByUsername(this.searchString);
      const filteredResults = new Array<SearchResult>();

      for (const result of results) {
        const chatStatus = await this.getChatStatusForUser(result.username);
        if (this.shouldShowInSearch(chatStatus)) {
          filteredResults.push(new SearchResult(result.username, result.gameName, result.roomId, result.roomName));
        }
      }

      return filteredResults;
    }
    finally {
      if (userSearchService) {
        userSearchService.dispose();
      }
    }
  }

  private shouldShowInSearch(userChatStatus: UserChatStatus): boolean{
    // All CMs can ban Standard Users
    if (userChatStatus === UserChatStatus.StandardUser){
      return true;
    }

    switch (this.cmUser.userChatStatus) {
      case UserChatStatus.ShiftLeader:
          if (userChatStatus === UserChatStatus.ChatModerator) {
            return true;
          } else {
            return false;
          }
      case UserChatStatus.TeamLeader:
          return true;
      default:
        return false; // a chatmod can only ban normal users so should be caught at the top
    }
  }

  private async getChatStatusForUser(username: string): Promise<UserChatStatus> {
    return await this.userChatStatusService.getChatStatusForUser(username);
  }

  private getCommandShortcutContext(): ICommandShortcutContext {
    const sinBanService: ISinBanService = this.$container.get(SERVICE_IDENTIFIERS.ISinBanService);
    const chatPrizeService: IChatPrizeService = this.$container.get(SERVICE_IDENTIFIERS.IChatPrizeService);
    const self = this;
    return {
      sinBinUser: (username: string) => sinBanService.sinBinUser(username, self.server),
      banUser: (username: string) => sinBanService.banUser(username, self.server),
      awardChatPrize: (usernames: string[]) => chatPrizeService.awardPlayerChatPrize(usernames, null, self.server),
      commandOrigin: CommandShortcutOrigin.SearchBar
    };
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/home/_components/search-bar/search-bar.component.ts